import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { replaceVariables } from 'PDF/TemplateEngine/utils/replaceVariables';
import { resolveLogoUrl } from 'PDF/TemplateEngine/utils/resolveLogoUrl';

// Header editable desde el editor low-code. Se monta como <View fixed> en la
// parte superior de cada página. Los campos de config soportan interpolación
// {var} contra `data` (aplanado con columnas tenant + filas tenant_texts).
//
// Config soportado:
//   - logo: URL absoluta o path relativo del logo (fallback a config.logo del
//           template si está vacío). Ancho fijo 2.8cm.
//   - leftLine: texto al lado del logo (puede quedar vacío).
//   - rightLine1 / rightLine2 / rightLine3: 3 líneas en columna derecha.
//   - borderColor: hex override del borde inferior (fallback colors.primary).
//
// Si un campo se resuelve a string vacío, la fila correspondiente no se
// renderiza (evita espacios en blanco desalineados).
function HeaderSection({ section, data, config }) {
  const { colors, FONT } = useTemplateColors();
  const cfg = section?.config || {};
  const borderColor = cfg.borderColor || colors.primary;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        header: {
          position: 'absolute',
          top: '0.8cm',
          left: '1.5cm',
          right: '1.5cm',
          height: '2cm',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1.5px solid ${borderColor}`,
          paddingBottom: '0.25cm',
        },
        left: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: '0.3cm',
          flex: 1,
        },
        right: {
          alignItems: 'flex-end',
        },
        logo: {
          width: '2.8cm',
          height: '1.4cm',
          objectFit: 'contain',
        },
        leftText: {
          fontSize: 11,
          fontFamily: FONT.bold,
          color: colors.darkText,
        },
        rightLinePrimary: {
          fontSize: 9,
          fontFamily: FONT.base,
          color: colors.darkText,
        },
        rightLineMuted: {
          fontSize: 9,
          fontFamily: FONT.base,
          color: colors.muted,
        },
      }),
    [colors, FONT, borderColor]
  );

  const logoSrc = resolveLogoUrl(cfg.logo || config?.logo || config?.portraitLogo);

  const leftLine = replaceVariables(cfg.leftLine || '', data || {});
  const rightLine1 = replaceVariables(cfg.rightLine1 || '', data || {});
  const rightLine2 = replaceVariables(cfg.rightLine2 || '', data || {});
  const rightLine3 = replaceVariables(cfg.rightLine3 || '', data || {});

  return (
    <View style={styles.header} fixed>
      <View style={styles.left}>
        {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
        {leftLine ? <Text style={styles.leftText}>{leftLine}</Text> : null}
      </View>
      <View style={styles.right}>
        {rightLine1 ? <Text style={styles.rightLinePrimary}>{rightLine1}</Text> : null}
        {rightLine2 ? <Text style={styles.rightLineMuted}>{rightLine2}</Text> : null}
        {rightLine3 ? <Text style={styles.rightLineMuted}>{rightLine3}</Text> : null}
      </View>
    </View>
  );
}

export default HeaderSection;
